/**
 * Task #1237 — Sezione DB quota nel dettaglio tenant.
 * Mostra: % utilizzo, byte, ultima ricalc, hard-block flags, embedding media bytes.
 * Azioni: recalc on-demand, modifica quota, sblocco emergenza, hard-block manuale.
 */
import { useEffect, useState, useCallback } from 'react';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Progress } from '@easyflow/ui-master/components/ui/progress';
import { Database, RefreshCw, Unlock, ShieldAlert, Edit2 } from 'lucide-react';
import { formatBytes } from '@/hooks/useMasterStorageQuota';
import { toErr, type MasterTenantDbQuotaDetail as DBQuotaDetail } from '@easyflow/shared';

const STATUS_BADGE: Record<string, JSX.Element> = {
  ok: <Badge className="bg-green-500/20 text-green-400 border-green-500/30">OK</Badge>,
  info: <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Info</Badge>,
  warning: <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Warning</Badge>,
  critical: <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">Critico</Badge>,
  grace: <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Grace 100%</Badge>,
  blocked: <Badge variant="destructive">BLOCCATO</Badge>,
};

export default function TenantDBQuotaSection({ tenantId }: { tenantId: string }): JSX.Element {
  const { toast } = useToast();
  const [data, setData] = useState<DBQuotaDetail | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await apiClient.get<DBQuotaDetail>(`/master/storage/db/tenants/${tenantId}`);
      if (r.success && r.data) setData(r.data);
    } catch {
      // best-effort
    }
  }, [tenantId]);

  useEffect(() => { void load(); }, [load]);

  const action = async (fn: () => Promise<unknown>, okMsg: string): Promise<void> => {
    setBusy(true);
    try {
      await fn();
      await load();
      toast({ title: okMsg });
    } catch (e: unknown) {
      const __e = toErr(e);
      toast({ title: 'Errore', description: __e?.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const recalc = (): Promise<void> => action(
    () => apiClient.post(`/master/storage/db/tenants/${tenantId}/recalc`, {}),
    'Ricalcolo completato',
  );

  const editQuota = (): Promise<void> | undefined => {
    if (!data) return;
    const v = window.prompt('Nuova quota DB in GB:', String(Math.round(data.dbMaxBytes / 1024 / 1024 / 1024)));
    if (!v) return;
    const gb = parseFloat(v);
    if (!Number.isFinite(gb) || gb <= 0) return;
    return action(
      () => apiClient.put(`/master/storage/db/tenants/${tenantId}/quota`, { dbMaxBytes: Math.floor(gb * 1024 * 1024 * 1024) }),
      'Quota aggiornata',
    );
  };

  const unblock = async (): Promise<void> => {
    // Round 5 fix #1 — motivazione obbligatoria, no fallback default.
    const raw = window.prompt('Motivo sblocco emergenza 24h (min 5 caratteri):', '');
    if (raw === null) return;
    const reason = raw.trim();
    if (reason.length < 5) {
      toast({ title: 'Motivazione obbligatoria', description: 'Inserisci almeno 5 caratteri per giustificare lo sblocco di emergenza.', variant: 'destructive' });
      return;
    }
    await action(
      () => apiClient.post(`/master/storage/db/tenants/${tenantId}/unblock`, { hours: 24, reason }),
      'Sblocco emergenza attivato (24h)',
    );
  };

  const hardBlock = (scope: 'db' | 'r2' | 'both'): Promise<void> | undefined => {
    if (!window.confirm(`Hard-block ${scope.toUpperCase()}?`)) return;
    return action(
      () => apiClient.post(`/master/storage/db/tenants/${tenantId}/block`, { scope }),
      `Block ${scope} attivato`,
    );
  };

  if (!data) {
    return (
      <GlassCard>
        <GlassCardContent className="p-6 text-sm text-muted-foreground">
          Caricamento quota DB…
        </GlassCardContent>
      </GlassCard>
    );
  }

  return (
    <GlassCard className="border-l-4 border-l-purple-500">
      <GlassCardHeader>
        <GlassCardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Database className="h-5 w-5 text-purple-600" /> Quota Database
          </span>
          {STATUS_BADGE[data.status]}
        </GlassCardTitle>
      </GlassCardHeader>
      <GlassCardContent className="space-y-4">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span>{formatBytes(data.dbUsedBytes)} / {formatBytes(data.dbMaxBytes)}</span>
            <span className="font-mono">{data.dbPct.toFixed(1)}%</span>
          </div>
          <Progress value={Math.min(100, data.dbPct)} />
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <span className="text-muted-foreground">Embedding media:</span>{' '}
            <span className="font-medium">{formatBytes(data.embeddingMediaUsedBytes)}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Last calc:</span>{' '}
            <span>{data.lastCalcAt ? new Date(data.lastCalcAt).toLocaleString('it-IT') : '—'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">DB hard-block:</span>{' '}
            {data.dbHardBlockedSince
              ? <span className="text-red-600">dal {new Date(data.dbHardBlockedSince).toLocaleString('it-IT')}</span>
              : <span className="text-emerald-600">no</span>}
          </div>
          <div>
            <span className="text-muted-foreground">R2 hard-block:</span>{' '}
            {data.r2HardBlockedSince
              ? <span className="text-red-600">dal {new Date(data.r2HardBlockedSince).toLocaleString('it-IT')}</span>
              : <span className="text-emerald-600">no</span>}
          </div>
          {data.emergencyUnblockUntil && (
            <div className="col-span-2 p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded">
              <span className="text-muted-foreground">Sblocco emergenza fino:</span>{' '}
              <span className="font-medium">{new Date(data.emergencyUnblockUntil).toLocaleString('it-IT')}</span>
              {data.emergencyUnblockReason && (
                <div className="text-xs italic text-muted-foreground mt-1">— {data.emergencyUnblockReason}</div>
              )}
            </div>
          )}
        </div>

        {data.alerts && data.alerts.length > 0 && (
          <div className="text-xs">
            <div className="text-muted-foreground mb-1">Alert inviati:</div>
            <div className="flex gap-2 flex-wrap">
              {data.alerts.map((a) => (
                <Badge key={a.pct} variant={a.sentAt ? 'default' : 'outline'}>
                  {a.pct}% {a.sentAt ? '✓' : '—'}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2 border-t border-border dark:border-slate-700">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void recalc()}>
            <RefreshCw className="h-3 w-3 mr-1" />Ricalcola
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void editQuota()}>
            <Edit2 className="h-3 w-3 mr-1" />Modifica quota
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void unblock()}>
            <Unlock className="h-3 w-3 mr-1" />Sblocco 24h
          </Button>
          <Button size="sm" variant="destructive" disabled={busy} onClick={() => void hardBlock('db')}>
            <ShieldAlert className="h-3 w-3 mr-1" />Block DB
          </Button>
          <Button size="sm" variant="destructive" disabled={busy} onClick={() => void hardBlock('r2')}>
            <ShieldAlert className="h-3 w-3 mr-1" />Block R2
          </Button>
        </div>
      </GlassCardContent>
    </GlassCard>
  );
}
